import { useState } from 'react';
import Container from '@/components/base/Container';
import Eyebrow from '@/components/base/Eyebrow';
import TitleReveal from '@/components/base/TitleReveal';
import { steps } from '@/mocks/services';

// Shown layer waits for the folder to settle (500ms), hidden layer leaves at once.
const SHOW = 'opacity-100 transition-opacity duration-300 delay-[480ms] motion-reduce:delay-0';
const HIDE = 'opacity-0 transition-opacity duration-150 delay-0 pointer-events-none';
const TAB_CLIP = '[clip-path:polygon(0_0,84%_0,100%_100%,0_100%)]';

export default function HowItWorks() {
  const [active, setActive] = useState(1);

  return (
    <section className="bg-background-50 py-20 md:py-32">
      <Container>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-16">
          <Eyebrow label="How It Works" />
          <div>
            <TitleReveal className="text-[34px] md:text-[48px] lg:text-[54px] font-medium leading-[1.05] tracking-[-0.025em] text-foreground-950">
              A Simpler Way to Get Things Fixed.
            </TitleReveal>
            <p className="mt-6 max-w-md text-[15px] md:text-base leading-relaxed text-foreground-600">
              Five clear steps from the first request to a working appliance, with no guesswork along the way.
            </p>
          </div>
        </div>

        <div className="mt-14 md:mt-20 flex flex-col gap-3 lg:flex-row lg:gap-0 lg:h-[460px] lg:[perspective:1800px]">
          {steps.map((s, i) => {
            const isActive = i === active;
            const num = `0${i + 1}`;
            const tone = isActive
              ? 'bg-primary-500'
              : i % 2 === 0 ? 'bg-background-100' : 'bg-background-200';
            return (
              <button
                key={s.title}
                type="button"
                aria-pressed={isActive}
                onClick={() => setActive(i)}
                onMouseEnter={() => setActive(i)}
                style={{ zIndex: isActive ? 20 : 10 - Math.abs(i - active) }}
                className={`group relative cursor-pointer text-left transition-[flex-grow,height,transform] duration-500 ease-out lg:h-full ${i > 0 ? 'lg:-ml-5' : ''} ${isActive ? 'h-[320px] lg:flex-[2.6]' : 'h-[104px] lg:flex-1'}`}
              >
                <div
                  className={`absolute inset-0 origin-left transition-transform duration-500 ease-out lg:[transform-style:preserve-3d] ${isActive ? 'lg:[transform:rotateY(0deg)]' : 'lg:[transform:rotateY(-26deg)] lg:group-hover:[transform:rotateY(-18deg)]'}`}
                >
                  {/* Folder tab */}
                  <span className={`absolute left-0 top-0 h-7 w-[58%] max-w-[180px] rounded-tl-lg transition-colors duration-500 ${TAB_CLIP} ${tone}`} />
                  {/* Folder body */}
                  <div className={`absolute inset-x-0 bottom-0 top-6 overflow-hidden rounded-lg rounded-tl-none border transition-colors duration-500 ${isActive ? 'border-primary-500' : 'border-background-300/60'} ${tone}`}>
                    {/* Collapsed layer: folder spine */}
                    <div className={`absolute inset-0 flex items-center justify-between gap-4 p-5 lg:flex-col lg:items-start lg:p-6 ${isActive ? HIDE : SHOW}`}>
                      <span className="whitespace-nowrap text-xs uppercase tracking-[0.14em] text-foreground-700 lg:[writing-mode:vertical-rl]">
                        {s.title}
                      </span>
                      <span className="text-3xl lg:text-4xl font-extralight tracking-tight text-foreground-400">{num}</span>
                    </div>
                    {/* Expanded layer: fixed width so text never reflows while the folder resizes */}
                    <div className={`absolute left-0 top-0 flex h-full w-[min(360px,100%)] flex-col justify-between p-6 md:p-7 text-white ${isActive ? SHOW : HIDE}`}>
                      <span className="text-xs uppercase tracking-[0.14em] text-white/80">{`Step ${num}`}</span>
                      <div>
                        <span className="block text-[72px] lg:text-[96px] font-extralight leading-none tracking-[-0.04em]">{num}</span>
                        <h3 className="mt-4 lg:mt-6 text-xl md:text-2xl font-normal tracking-tight">{s.title}</h3>
                        <p className="mt-3 w-[300px] max-w-full text-sm md:text-[15px] leading-relaxed text-white/85">{s.text}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </Container>
    </section>
  );
}